import React, {useCallback, useEffect, useMemo, useState} from "react";
import {db} from '../../../firebase/FirebaseProvider'
import {
    doc,
    collection,
    getDoc,
    getDocs,
    query,
    where,
    updateDoc
} from 'firebase/firestore'
import {Link, useParams} from 'react-router-dom';
import {Location, User, Visit} from "../../types/Types";
import {Button, Card} from "react-bootstrap";
import LocationRow from "../LocationRow";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import UserNav from "../UserNav";
import UserAdmin from "../UserAdmin";
import PageTracking from "../../auth/PageTracking";
import DateTime from "../../date/DateTime";
import VerificationTag from "../../tag/VerificationTag";
import {getCountryFlag} from "../../util/Util";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import V2YearPicker from "../../shared/v2/V2YearPicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/free-solid-svg-icons";

interface Props {
    locations: Array<Location> | undefined,
    currentUser: User | undefined,
}

function getVisitYear(value: any): number {
    if (value?.seconds) {
        return new Date(value.seconds * 1000).getFullYear();
    }
    if (value?.toDate) {
        return value.toDate().getFullYear();
    }
    return new Date(value).getFullYear();
}

function UserVisits(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [user, setUser] = useState<User | undefined>(undefined);
    const [processedLocations, setProcessedLocations] = useState<Array<Location> | undefined>(undefined);
    const [visits, setVisits] = useState<Array<Visit>>(new Array<Visit>());
    const [visitsMap, setVisitsMap] = useState<Map<string, Visit> | undefined>(undefined);
    const [year, setYear] = useState<string>('1');
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const loadAllData = useCallback(async() => {
        if(!user) {
            return;
        }

        // Load visits
        const visitsMap: Map<string, Visit> = new Map<string, Visit>();
        const visitsArray: Array<Visit> = new Array<Visit>();
        const visitsOnceCollection = query(collection(db, 'visits'), where('userId', '==', user.id));
        const visitsCollectionSnapshot = await getDocs(visitsOnceCollection);
        visitsCollectionSnapshot.forEach((doc) => {
            let tempVisit: Visit = doc.data() as Visit;
            visitsMap.set(tempVisit.locationId, tempVisit);
            visitsArray.push(tempVisit)
        });
        console.log(`Loaded user visits: ${visitsArray.length} documents`);
        setVisitsMap(visitsMap);
        setVisits(visitsArray)
    }, [user]);

    const updateUserVisitCount = useCallback(async (userId: string, correctedTotal: number) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { visitCount: correctedTotal });
            console.log(`Updated visitCount for user ${userId} to ${correctedTotal}`);
        } catch (error) {
            console.error(`Failed to update visitCount for user ${userId}:`, error);
        }
    }, []);

    const processLocations = useCallback(async() => {
        const visits = visitsMap as Map<string, Visit>;
        if(!props.locations) {
            return [];
        }

        if (visits && user) {
            const locationIdSet = new Set(props.locations.map(l => l.id));
            const totalVisits = Array.from(visits.values()).filter(v => v.visited).length;
            const ghostVisits = Array.from(visits.values()).filter(v => v.visited && !locationIdSet.has(v.locationId));
            const correctedTotal = totalVisits - ghostVisits.length;
            if (correctedTotal !== user.visitCount) {
                updateUserVisitCount(user.id, correctedTotal);
            }
        }

        props.locations.forEach((location) => {
            if (user && visitsMap) {
                let visitCheck = visits.get(location.id);
                if (visitCheck && visitCheck.visited) {
                    location.visited = true;
                } else {
                    location.visited = false;
                }
            }
        });
        const locationsSorted = props.locations
            .filter(location => location.visited && (location.enabled === undefined || location.enabled === true))
            .sort((locationA, locationB) => {
                if(locationA.name > locationB.name) {
                    return 1;
                } else if(locationA.name === locationB.name)  {
                    return 0;
                } else {
                    return -1;
                }
            });
        setProcessedLocations(locationsSorted);
    }, [props.locations, visitsMap, user, updateUserVisitCount]);

    useEffect(() => {
        if (!id) return;

        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                let userToSet = docSnap.data() as User;
                userToSet.id = docSnap.id;
                setUser(userToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            await loadAllData();
        })();
    }, [user, loadAllData]);

    useEffect(() => {
        if (!props.locations || !visitsMap) return;
        (async () => {
            await processLocations();
        })();
    }, [props.locations, visitsMap, processLocations]);

    const displayedProcessedLocations = useMemo(() => {
        if (!processedLocations || !visitsMap || year === '1') {
            return processedLocations;
        }

        return processedLocations.filter((location) => {
            const visit = visitsMap.get(location.id);
            return visit && getVisitYear(visit.lastModified) === Number(year);
        });
    }, [processedLocations, visitsMap, year]);

    const displayedVerifiedVisitCount = useMemo(() => {
        if (!displayedProcessedLocations || !visitsMap) {
            return 0;
        }
        return displayedProcessedLocations.filter((location) => visitsMap.get(location.id)?.verified).length;
    }, [displayedProcessedLocations, visitsMap]);

    const availableVisitYears = useMemo(() => {
        if (!processedLocations || !visitsMap) return [];
        return Array.from(new Set(
            processedLocations
                .map((location) => visitsMap.get(location.id))
                .filter((visit): visit is Visit => !!visit)
                .map((visit) => getVisitYear(visit.lastModified))
        ));
    }, [processedLocations, visitsMap]);

    const downloadVisitsAsCSV = () => {
        if (!displayedProcessedLocations || !visitsMap || !props.locations) return;

        // Create CSV header
        const headers = ['Name', 'Address', 'City', 'Country', 'Latitude', 'Longitude', 'Visited Date', 'Verified Check In'];
        const csvRows = [headers.join(',')];

        // Add data rows
        displayedProcessedLocations.forEach(location => {
            const visit = visitsMap.get(location.id);
            if (visit && visit.visited && (location.enabled === undefined || location.enabled === true)) {
                let visitDate = 'N/A';
                if (visit.lastModified) {
                    // Handle both Date objects and Firestore Timestamps
                    const dateObj = visit.lastModified instanceof Date 
                        ? visit.lastModified 
                        : (visit.lastModified as any).toDate ? (visit.lastModified as any).toDate() : new Date(visit.lastModified);
                    
                    // Format as YYYY-MM-DD for Excel compatibility
                    visitDate = dateObj.toISOString().split('T')[0];
                }
                const verifiedCheckIn = visit.verified ? 'Yes' : 'No';
                const row = [
                    `"${location.name || ''}"`,
                    `"${location.address || ''}"`,
                    `"${location.city || ''}"`,
                    `"${location.country || ''}"`,
                    location.lat || '',
                    location.lng || '',
                    visitDate,
                    verifiedCheckIn
                ];
                csvRows.push(row.join(','));
            }
        });

        // Create blob and download
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const currentDate = new Date().toISOString().split('T')[0];
        link.setAttribute('href', url);
        link.setAttribute('download', `${user?.name || 'user'}_visits_${currentDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openLocations = props.locations?.filter(loc => !loc.pubIsClosed && !loc.pubIsTemporaryClosed && (loc.enabled === undefined || loc.enabled === true)) || [];
    const displayedLocationIds = new Set((displayedProcessedLocations || []).map((location) => location.id));
    const visitedOpenPercentage = openLocations.length > 0 && displayedProcessedLocations
        ? Math.round((openLocations.filter(loc => displayedLocationIds.has(loc.id)).length / openLocations.length) * 100)
        : 0;
    const v2DetailHeader = (
        <div className="user-v2-subpage-header-content">
            <div className="user-v2-subpage-header-stats">
                <span>User has visited <b>{visitedOpenPercentage}%</b> of open pubs</span>
            </div>
            <V2YearPicker value={year} onChange={setYear} availableYears={availableVisitYears} ariaLabel="Select visit year" />
        </div>
    );

    const v2DetailActions = props.currentUser && user && props.currentUser.id === user.id ? (
        <Button
            variant="primary"
            size="sm"
            className="user-v2-download-button"
            onClick={downloadVisitsAsCSV}
            disabled={!displayedProcessedLocations || displayedProcessedLocations.length === 0}
            aria-label="Download visits as CSV"
        >
            <FontAwesomeIcon icon={faDownload} />
        </Button>
    ) : undefined;


    return (
        <>
            <PageTracking />
            {isUiV2 ? <UserDetailsV2
                currentUser={props.currentUser}
                user={user}
                locations={props.locations}
                detailHeader={v2DetailHeader}
                detailActions={v2DetailActions}
                detailStatValues={{
                    "Visits": displayedProcessedLocations?.length || 0,
                    "Verified": displayedVerifiedVisitCount,
                }}
            /> : <UserDetails user={user}/>}
            {(props.currentUser && props.currentUser.userAdmin) && <UserAdmin user={user}/>}    
            {!isUiV2 && <UserNav currentUser={props.currentUser} user={user}/>}
            {isUiV2 ? (
                <V2GlassPanel className="user-v2-visits-list-card v2-glass-list-panel">
                    {displayedProcessedLocations && visitsMap && displayedProcessedLocations.map((location: Location) => {
                        const visit = visitsMap.get(location.id);
                        if (!visit) return null;

                        return (
                            <V2GlassSurface key={location.id} className="user-v2-visit-row v2-glass-list-row">
                                <div className="user-v2-visit-main">
                                    <div className="user-v2-visit-title-line">
                                        <Link to={`/location/${location.id}/comments`} className="user-v2-location-name-link">
                                            <V2GlassChip className="v2-glass-location-chip">{location.name}</V2GlassChip>
                                        </Link>
                                        {visit.verified && <VerificationTag active={false}/>}
                                    </div>
                                    <div className="user-v2-visit-meta">
                                        <span className="user-v2-visit-country">{getCountryFlag(location.country || '')}</span>
                                        <span className="user-v2-visit-address">{location.address}</span>
                                    </div>
                                </div>
                                <V2GlassChip className="user-v2-visit-date-chip">
                                    <DateTime date={visit.lastModified}/>
                                </V2GlassChip>
                            </V2GlassSurface>
                        );
                    })}
                    {displayedProcessedLocations && displayedProcessedLocations.length === 0 && <div className="user-v2-empty-list">No visits for this year</div>}
                    {!processedLocations && <div className="user-v2-empty-list">Loading...</div>}
                </V2GlassPanel>
            ) : <Card className="mb-3">
                {!isUiV2 && (visits) && <Card.Header className={"header-padding"}>
                    {(visitsMap !== undefined) && 
                     <>
                            <div className={"float-left stats-card mr-1"}>
                                <span>Total: <b>{user?.visitCount}</b></span>
                            </div>
                            <div className={"float-left stats-card mr-2"}>
                                {(() => {
                                    if (!props.locations || !visitsMap) return null;
                                    const openPubs = props.locations.filter(loc => !loc.pubIsClosed && !loc.pubIsTemporaryClosed && (loc.enabled === undefined || loc.enabled === true));
                                    const visitedOpenPubs = openPubs.filter(loc => {
                                        const visit = visitsMap.get(loc.id);
                                        return visit && visit.visited;
                                    });
                                    const percent = openPubs.length > 0 ? Math.round((visitedOpenPubs.length / openPubs.length) * 100) : 0;
                                    return <span><b>{percent}%</b></span>;
                                })()}
                            </div>
                        </>
                    }
                    {((user?.sessionVerifiedCount || 0 > 0) && visitsMap !== undefined) && 
                        <>
                            <div className={"float-left stats-card mr-1"}>
                                <span>Verified: <b>{user?.sessionVerifiedCount}</b></span>
                            </div>
                            <div className={"float-left stats-card"}>
                                {(() => {
                                    if (!props.locations || !visitsMap) return null;
                                    const openPubs = props.locations.filter(loc => !loc.pubIsClosed && !loc.pubIsTemporaryClosed && (loc.enabled === undefined || loc.enabled === true));
                                    const verifiedOpenPubs = openPubs.filter(loc => {
                                        const visit = visitsMap.get(loc.id);
                                        return visit && visit.verified;
                                    });
                                    const percent = openPubs.length > 0 ? Math.round((verifiedOpenPubs.length / openPubs.length) * 100) : 0;
                                    return <span><b>{percent}%</b></span>;
                                })()}
                            </div>
                        </>
                }
                    {props.currentUser && user && props.currentUser.id === user.id && (
                        <div className={"float-right"}>
                            <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={downloadVisitsAsCSV}
                                disabled={!displayedProcessedLocations || displayedProcessedLocations.length === 0}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </Button>
                        </div>
                    )}
                </Card.Header>
                }           
                <Card.Body className={"p-0"}>
                    {processedLocations && <>
                        {visitsMap && processedLocations.map((location: Location, index: number) => (
                            <LocationRow key={location.id} visit={visitsMap.get(location.id)} index={index} location={location} />
                        ))}
                    </>}
                    {!processedLocations && <>
                        <div className={"loading"}>Loading...</div>
                    </>}
                </Card.Body>
            </Card>}
        </>
    );
}

export default UserVisits;
